import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { HandHeart, Calendar, Clock, CheckCircle2, AlertTriangle, MapPin, Phone } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ScheduleInterviewPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [booking, setBooking] = useState(false);
  const [booked, setBooked] = useState(null);

  useEffect(() => { fetchBookingInfo(); }, [token]);

  const fetchBookingInfo = async () => {
    try {
      const res = await axios.get(`${API}/interview-scheduling/book/${token}`);
      setData(res.data);
      if (res.data.status === 'already_booked') {
        setBooked({ date: res.data.booked_date, time: res.data.booked_time });
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'This scheduling link is invalid or has expired.');
    } finally {
      setLoading(false);
    }
  };

  const handleBook = async () => {
    if (!selectedSlot) return;
    setBooking(true);
    try {
      const res = await axios.post(`${API}/interview-scheduling/book/${token}`, { slot_id: selectedSlot.id });
      setBooked({ date: res.data.date, time: res.data.time, duration: res.data.duration_minutes });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to book. Please try again.');
    } finally {
      setBooking(false);
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (timeStr) => {
    const [h, m] = timeStr.split(':');
    const hour = parseInt(h);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${h12}:${m} ${ampm}`;
  };

  // Group slots by date
  const slotsByDate = {};
  if (data?.available_slots) {
    data.available_slots.forEach(slot => {
      if (!slotsByDate[slot.date]) slotsByDate[slot.date] = [];
      slotsByDate[slot.date].push(slot);
    });
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#4B2D6E]/5 to-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#4B2D6E] border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#4B2D6E]/5 to-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <AlertTriangle className="h-14 w-14 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-[#333] mb-2">Link Unavailable</h2>
            <p className="text-[#666]">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const interviewLabel = data?.interview_type === 'phone' ? 'Phone Interview' : 'In-Person Interview';

  // Already booked state
  if (booked) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#4B2D6E]/5 to-white">
        <header className="bg-[#4B2D6E] text-white py-5 px-6">
          <div className="max-w-2xl mx-auto flex items-center gap-3">
            <HandHeart className="h-8 w-8 text-[#E8722A]" />
            <span className="font-bold font-[Lato] text-lg">{data?.company_name || 'SeniorPeerConnect'}</span>
          </div>
        </header>
        <div className="max-w-2xl mx-auto p-6 mt-10">
          <Card className="border-t-4 border-green-500">
            <CardContent className="pt-8 pb-8 text-center">
              <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-[#4B2D6E] mb-2">Interview Confirmed!</h2>
              <p className="text-[#666] mb-6">Your {interviewLabel.toLowerCase()} has been scheduled.</p>
              <div className="inline-flex flex-col items-start gap-3 bg-[#F5F5F5] rounded-xl p-6 text-left">
                <div className="flex items-center gap-3">
                  <Calendar className="h-5 w-5 text-[#4B2D6E]" />
                  <span className="font-medium">{formatDate(booked.date)}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="h-5 w-5 text-[#4B2D6E]" />
                  <span className="font-medium">{formatTime(booked.time)}</span>
                  {booked.duration && <span className="text-sm text-[#999]">({booked.duration} min)</span>}
                </div>
                {data?.interview_type === 'phone' && (
                  <div className="flex items-center gap-3">
                    <Phone className="h-5 w-5 text-[#4B2D6E]" />
                    <span className="text-sm text-[#666]">We will call you at the scheduled time</span>
                  </div>
                )}
                {data?.interview_type !== 'phone' && data?.interview_location && (
                  <div className="flex items-center gap-3">
                    <MapPin className="h-5 w-5 text-[#4B2D6E]" />
                    <span className="text-sm text-[#666]">{data.interview_location}</span>
                  </div>
                )}
              </div>
              <p className="text-sm text-[#999] mt-6">A confirmation has been sent to your email and phone.</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Scheduling state
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#4B2D6E]/5 to-white">
      <header className="bg-[#4B2D6E] text-white py-5 px-6">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <HandHeart className="h-8 w-8 text-[#E8722A]" />
          <span className="font-bold font-[Lato] text-lg">{data?.company_name || 'SeniorPeerConnect'}</span>
        </div>
      </header>

      <div className="max-w-2xl mx-auto p-6 mt-6" data-testid="schedule-interview-page">
        <div className="mb-8 text-center">
          <Badge className="bg-[#E8722A]/10 text-[#E8722A] mb-3 text-sm">{interviewLabel}</Badge>
          <h1 className="text-3xl font-bold text-[#4B2D6E] font-[Lato] mb-2">
            Schedule Your {interviewLabel}
          </h1>
          {data?.candidate_name && (
            <p className="text-[#666]">
              Hi {data.candidate_name}! Pick a time that works best for you.
            </p>
          )}
          <p className="text-sm text-[#999] mt-1">
            {data?.job_title} — {data?.company_name}
          </p>
        </div>

        {Object.keys(slotsByDate).length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Calendar className="h-12 w-12 mx-auto text-gray-300 mb-3" />
              <p className="text-[#666]">No available time slots right now.</p>
              <p className="text-sm text-[#999] mt-1">Please check back later or contact us directly.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {Object.entries(slotsByDate).map(([date, slots]) => (
              <div key={date}>
                <h3 className="font-semibold text-[#4B2D6E] mb-3 flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  {formatDate(date)}
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {slots.map(slot => (
                    <button
                      key={slot.id}
                      onClick={() => setSelectedSlot(selectedSlot?.id === slot.id ? null : slot)}
                      className={`p-4 rounded-xl border-2 text-center transition-all ${
                        selectedSlot?.id === slot.id
                          ? 'border-[#E8722A] bg-[#E8722A]/5 ring-2 ring-[#E8722A]/30'
                          : 'border-gray-200 hover:border-[#4B2D6E]/30 bg-white'
                      }`}
                      data-testid={`slot-${slot.id}`}
                    >
                      <p className="font-semibold text-[#333] text-lg">{formatTime(slot.start_time)}</p>
                      <p className="text-xs text-[#999] mt-1">{slot.duration_minutes} min</p>
                    </button>
                  ))}
                </div>
              </div>
            ))}

            {selectedSlot && (
              <Card className="border-[#E8722A] border-2 mt-6">
                <CardContent className="py-5">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                      <p className="text-sm text-[#666]">Selected time:</p>
                      <p className="font-bold text-[#4B2D6E] text-lg">
                        {formatDate(selectedSlot.date)} at {formatTime(selectedSlot.start_time)}
                      </p>
                    </div>
                    <Button
                      onClick={handleBook}
                      disabled={booking}
                      className="bg-[#E8722A] hover:bg-[#D6621F] text-white px-8 py-3 text-base"
                      data-testid="confirm-booking-btn"
                    >
                      {booking ? 'Confirming...' : 'Confirm Interview'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ScheduleInterviewPage;
